import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { AuthProvider, useAuth } from './context/AuthContext';
import { HighlightedStoresProvider } from './context/HighlightedStoresContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { NavigationProvider } from './context/NavigationContext';
import {
  Login,
  Register,
  RegistrationSuccess,
  ForgotPassword,
  ResetSent,
  ResetPassword,
  ResetSuccess,
  VerifyEmail,
} from './components/Auth';
import { Dashboard } from './components/Dashboard';
import { LanguageThemeSelector } from './components/common';
import { PricingPage } from './components/Pricing';
import { OfferPage } from './components/Offer';
import { LandingPage } from './components/Landing';
import { Footer } from './components/common';
import './App.css';

// Normalize path: no trailing slash, so /landing/ and /landing match the same route
const normalizePath = (path) => (path || '/').replace(/\/+$/, '') || '/';

const isAuthPath = (path) =>
  path === '/login' ||
  path === '/register' ||
  /^\/verify-email\/[^/]+$/.test(path) ||
  /^\/reset-password\/[^/]+$/.test(path);

// Toggle for Login / Register at top of auth card
const AuthViewToggle = ({ currentView, onSwitch }) => {
  const { t } = useLanguage();
  const isLogin = currentView === 'login';
  return (
    <div className="auth-view-toggle">
      <div className="auth-view-toggle__track">
        <button
          type="button"
          className={`auth-view-toggle__tab ${isLogin ? 'auth-view-toggle__tab--active' : ''}`}
          onClick={() => onSwitch('login')}
        >
          {t('auth.login')}
        </button>
        <button
          type="button"
          className={`auth-view-toggle__tab ${!isLogin ? 'auth-view-toggle__tab--active' : ''}`}
          onClick={() => onSwitch('register')}
        >
          {t('auth.register')}
        </button>
        <span
          className="auth-view-toggle__slider"
          style={{ transform: isLogin ? 'translateX(0)' : 'translateX(100%)' }}
        />
      </div>
    </div>
  );
};

// Auth Page Component - handles all authentication flows
const AuthPage = () => {
  const [view, setView] = useState('login');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);

  // Check URL for auth route and tokens (password reset or email verification)
  useEffect(() => {
    const path = normalizePath(window.location.pathname);

    const verifyEmailMatch = path.match(/^\/verify-email\/([^/]+)$/);
    const resetPasswordMatch = path.match(/^\/reset-password\/([^/]+)$/);

    if (verifyEmailMatch) {
      setVerifyToken(verifyEmailMatch[1]);
      setView('verifyEmail');
    } else if (resetPasswordMatch) {
      setResetToken(resetPasswordMatch[1]);
      setView('resetPassword');
    } else if (path === '/register') {
      setView('register');
    } else if (path === '/login') {
      setView('login');
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const resetTokenParam = urlParams.get('reset_token');
      const verifyTokenParam = urlParams.get('verify_token');

      if (resetTokenParam) {
        setResetToken(resetTokenParam);
        setView('resetPassword');
      } else if (verifyTokenParam) {
        setVerifyToken(verifyTokenParam);
        setView('verifyEmail');
      }
    }
  }, []);

  const handleRegistrationSuccess = (email) => {
    setRegisteredEmail(email);
    setView('registrationSuccess');
  };

  const handleResetSent = (email) => {
    setResetEmail(email);
    setView('resetSent');
  };

  const handleResetSuccess = () => {
    setView('resetSuccess');
    window.history.replaceState({}, document.title, '/login');
  };

  const handleVerifySuccess = () => {
    setView('login');
    window.history.replaceState({}, document.title, '/login');
  };

  const handleSwitchToLogin = () => {
    setView('login');
    window.history.replaceState({}, document.title, '/login');
  };

  const renderView = () => {
    switch (view) {
      case 'login':
        return (
          <Login
            onSwitchToRegister={() => setView('register')}
            onSwitchToForgotPassword={() => setView('forgotPassword')}
          />
        );
      case 'register':
        return (
          <Register
            onSwitchToLogin={() => setView('login')}
            onRegistrationSuccess={handleRegistrationSuccess}
          />
        );
      case 'registrationSuccess':
        return (
          <RegistrationSuccess
            email={registeredEmail}
            onSwitchToLogin={() => setView('login')}
          />
        );
      case 'forgotPassword':
        return (
          <ForgotPassword
            onSwitchToLogin={() => setView('login')}
            onResetSent={handleResetSent}
          />
        );
      case 'resetSent':
        return (
          <ResetSent
            email={resetEmail}
            onSwitchToLogin={() => setView('login')}
          />
        );
      case 'resetPassword':
        return (
          <ResetPassword
            token={resetToken}
            onSuccess={handleResetSuccess}
            onSwitchToLogin={handleSwitchToLogin}
          />
        );
      case 'resetSuccess':
        return <ResetSuccess onSwitchToLogin={() => setView('login')} />;
      case 'verifyEmail':
        return (
          <VerifyEmail
            token={verifyToken}
            onSuccess={handleVerifySuccess}
            onSwitchToLogin={handleSwitchToLogin}
          />
        );
      default:
        return (
          <Login
            onSwitchToRegister={() => setView('register')}
            onSwitchToForgotPassword={() => setView('forgotPassword')}
          />
        );
    }
  };

  const showAuthToggle = view === 'login' || view === 'register';
  const isFlipped = view === 'register';

  return (
    <div className="auth-page">
      <div className="auth-controls">
        <LanguageThemeSelector />
      </div>
      <div className="auth-content">
        {showAuthToggle && (
          <>
            <AuthViewToggle
              currentView={view}
              onSwitch={(nextView) => {
                setView(nextView);
                window.history.replaceState(
                  {},
                  document.title,
                  nextView === 'register' ? '/register' : '/login'
                );
              }}
            />
            <div className="auth-card-flip">
              <div className={`auth-card-flip__inner${isFlipped ? ' auth-card-flip__inner--flipped' : ''}`}>
                <div className="auth-card-face auth-card-face--front">
                  <Login
                    onSwitchToRegister={() => setView('register')}
                    onSwitchToForgotPassword={() => setView('forgotPassword')}
                  />
                </div>
                <div className="auth-card-face auth-card-face--back">
                  <Register
                    onSwitchToLogin={() => setView('login')}
                    onRegistrationSuccess={handleRegistrationSuccess}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {!showAuthToggle && renderView()}
      </div>
      <Footer />
    </div>
  );
};

// Main App Content - decides between Auth, Dashboard, or Public Pages
const AppContent = () => {
  const { isAuthenticated, loading, connectionIssue, dismissConnectionIssue } = useAuth();
  const { t } = useLanguage();
  const [currentPath, setCurrentPath] = useState(() => normalizePath(window.location.pathname));

  const maintenanceBanner =
    connectionIssue === 'maintenance' ? (
      <Alert
        variant="warning"
        className="app-maintenance-banner mb-0 rounded-0 border-0 text-center py-2"
        dismissible
        onClose={dismissConnectionIssue}
      >
        {t('errors.serviceUnavailable')}
      </Alert>
    ) : null;

  // Listen to pathname changes (popstate from back/forward or dispatchEvent from in-app nav)
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(normalizePath(window.location.pathname));
    };

    window.addEventListener('popstate', handleLocationChange);
    handleLocationChange();

    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const path = currentPath;

  // Public pages (accessible without authentication). Key forces full unmount on route change so the language selector does not duplicate.
  if ((path === '/' || path === '/landing') && !isAuthenticated) {
    return (
      <div key="landing" className="public-page-wrapper">
        <LandingPage />
        <Footer />
      </div>
    );
  }

  if (path === '/pricing') {
    return (
      <div key="pricing" className="public-page-wrapper">
        <PricingPage />
        <Footer />
      </div>
    );
  }

  if (path === '/offer') {
    return (
      <div key="offer" className="public-page-wrapper">
        <OfferPage />
        <Footer />
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        {maintenanceBanner}
        <Dashboard key="dashboard" />
      </>
    );
  }

  if (isAuthPath(path)) {
    return <AuthPage key="auth" />;
  }

  // Unauthenticated visitors on unknown paths see the landing page
  return (
    <div key="landing-fallback" className="public-page-wrapper">
      <LandingPage />
      <Footer />
    </div>
  );
};

// Main App Component with Providers
function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <NavigationProvider>
          <AuthProvider>
            <HighlightedStoresProvider>
              <AppContent />
            </HighlightedStoresProvider>
          </AuthProvider>
        </NavigationProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
